import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { monitorForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";

import { DataRoomFolderBrowseFragmentFragment } from "@/src/graphql/generated";
import { CopyIcon, FolderOpenIcon } from "lucide-react";
import { Folders } from "./Folders";
import { Files } from "./Files";
import { EditFile } from "@/src/components/data_room/EditFile";
import { DeleteFile } from "../DeleteFileModal";
import { MoveFileModal } from "../MoveFileModal";
import { EditFolder } from "@/src/components/data_room/EditFolder";
import { MoveFolderModal } from "../MoveFolderModal";
import { MoveItemsModal } from "../MoveItemsModal";
import { DeleteItemsModal } from "../DeleteItemsModal";
import { DataRoomTableHeader } from "../table/header";
import { Button } from "@/src/components/tailwind/Button";
import { CopyFolderStructureModal } from "../CopyFolderStructureModal";
import { useMoveItemsWithUndo } from "../dnd/useMoveItemsWithUndo";
import { isDataRoomDragData } from "../dnd/types";
import { clearAllFolderDropHighlights } from "../dnd/useFolderDropTarget";
import {
  clearCollapseIntoDragAnimation,
  setCollapseCursorTarget,
} from "../dnd/collapseIntoDrag";
import {
  ExternalDropTarget,
  useExternalFileDrop,
} from "../dnd/useExternalFileDrop";
import { useDataRoomExternalUpload } from "../dnd/useDataRoomExternalUpload";
import { useDataRoomListSelection } from "../dnd/useDataRoomListSelection";
import { useMarqueeSelection } from "../dnd/useMarqueeSelection";
import { MarqueeGutterStrips } from "../dnd/MarqueeGutterStrips";
import { classNames } from "@/src/utils/cn";
import { DataRoomContext } from "..";
import { getVisibleDataRoomListItems } from "./visibleItems";

interface FolderContentProps {
  folder?: DataRoomFolderBrowseFragmentFragment;
  isLoading: boolean;
  error?: unknown;
  selectedFolders: string[];
  selectedFiles: string[];
  setSelectedFolders: (folders: string[]) => void;
  setSelectedFiles: (files: string[]) => void;
  triggerMoveItems: boolean;
  setTriggerMoveItems: (trigger: boolean) => void;
  triggerDeleteItems: boolean;
  setTriggerDeleteItems: (trigger: boolean) => void;
}

type FolderContextType = {
  isDragging: boolean;
  onItemsDropped: (args: {
    targetFolderId: string;
    targetFolderName: string;
    fileIds: string[];
    folderIds: string[];
  }) => void;
  registerExternalFolderTarget: (
    target: ExternalDropTarget,
    element: HTMLElement | null,
  ) => void;
  selectItem: (
    id: string,
    type: "file" | "folder",
    opts?: { shiftKey?: boolean },
  ) => void;

  editFileId: string;
  setEditFileId: (fileId: string) => void;
  moveFileId: string;
  setMoveFileId: (fileId: string) => void;
  deleteFileId: string;
  setDeleteFileId: (fileId: string) => void;

  editFolderId: string;
  setEditFolderId: (folderId: string) => void;
  moveFolderId: string;
  setMoveFolderId: (folderId: string) => void;
};

export const FolderContext = createContext<FolderContextType>({
  isDragging: false,
  onItemsDropped: () => {},
  registerExternalFolderTarget: () => {},
  selectItem: () => {},
  editFileId: "",
  setEditFileId: () => {},
  moveFileId: "",
  setMoveFileId: () => {},
  deleteFileId: "",
  setDeleteFileId: () => {},
  editFolderId: "",
  setEditFolderId: () => {},
  moveFolderId: "",
  setMoveFolderId: () => {},
});

export function FolderView(props: FolderContentProps) {
  const [deleteFileId, setDeleteFileId] = useState<string>("");
  const [editFileId, setEditFileId] = useState<string>("");
  const [moveFileId, setMoveFileId] = useState<string>("");
  const [editFolderId, setEditFolderId] = useState<string>("");
  const [moveFolderId, setMoveFolderId] = useState<string>("");
  const [isDragging, setIsDragging] = useState(false);
  const [showCopyStructureModal, setShowCopyStructureModal] =
    useState<boolean>(false);

  const { moveItems } = useMoveItemsWithUndo();
  const { handleExternalDrop } = useDataRoomExternalUpload();
  const { filters, indexingEnabled } = useContext(DataRoomContext);
  const selectionSurfaceRef = useRef<HTMLDivElement>(null);

  const surfaceTarget = useMemo<ExternalDropTarget | null>(() => {
    if (!props.folder) return null;
    const permission = props.folder.permissions[0]
      ? {
          id: props.folder.permissions[0].id,
          name: props.folder.permissions[0].name,
          description: props.folder.permissions[0].description,
        }
      : null;
    return {
      folderId: props.folder.id,
      folderName: props.folder.name || "Home",
      permission,
    };
  }, [props.folder]);

  const canExternalDrop = Boolean(
    props.folder?.hasFolderWriteAccess && surfaceTarget,
  );

  const {
    isSurfaceDragOver,
    registerFolderTarget,
    surfaceHandlers,
  } = useExternalFileDrop({
    enabled: canExternalDrop,
    surfaceTarget,
    onDrop: ({ files, folders, target }) => {
      handleExternalDrop({
        files,
        folders,
        target,
        allowAutoUpload: true,
      });
    },
  });

  const visibleItems = useMemo(() => {
    if (!props.folder) return [];
    return getVisibleDataRoomListItems(
      props.folder,
      filters,
      indexingEnabled,
    );
  }, [props.folder, filters, indexingEnabled]);

  const { selectItem, selectItemsByIds } = useDataRoomListSelection({
    items: visibleItems,
    selectedFiles: props.selectedFiles,
    selectedFolders: props.selectedFolders,
    setSelectedFiles: props.setSelectedFiles,
    setSelectedFolders: props.setSelectedFolders,
  });

  const {
    overlayRef: marqueeOverlayRef,
    onSurfaceMouseDown,
    onGutterMouseDown,
  } = useMarqueeSelection({
    enabled: Boolean(props.folder),
    containerRef: selectionSurfaceRef,
    onSelectIds: selectItemsByIds,
  });

  const onItemsDropped = useCallback(
    (args: {
      targetFolderId: string;
      targetFolderName: string;
      fileIds: string[];
      folderIds: string[];
    }) => {
      if (!props.folder) {
        return;
      }

      void moveItems({
        sourceFolderId: props.folder.id,
        targetFolderId: args.targetFolderId,
        targetFolderName: args.targetFolderName,
        fileIds: args.fileIds,
        folderIds: args.folderIds,
        onMoved: () => {
          props.setSelectedFiles([]);
          props.setSelectedFolders([]);
        },
      });
    },
    [
      moveItems,
      props.folder,
      props.setSelectedFiles,
      props.setSelectedFolders,
    ],
  );

  useEffect(() => {
    return monitorForElements({
      canMonitor: ({ source }) => isDataRoomDragData(source.data),
      onDragStart: ({ location }) => {
        setIsDragging(true);
        setCollapseCursorTarget(
          location.current.input.clientX,
          location.current.input.clientY,
        );
      },
      onDrag: ({ location }) => {
        setCollapseCursorTarget(
          location.current.input.clientX,
          location.current.input.clientY,
        );
      },
      onDrop: () => {
        setIsDragging(false);
        clearAllFolderDropHighlights();
        clearCollapseIntoDragAnimation();
      },
    });
  }, []);

  if (props.error) {
    return (
      <div>
        <p>Something went wrong</p>
      </div>
    );
  }

  if (props.isLoading || !props.folder) {
    return (
      <div className="p-2 w-full">
        <div className="animate-pulse">
          <div className="space-y-4">
            {[...Array(12)].map((_, i) => (
              <div key={i} className="flex gap-x-4">
                <div className="h-4 bg-gray-200 rounded w-24"></div>
                <div className="h-4 bg-gray-200 rounded w-96"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
                <div className="h-4 bg-gray-200 rounded w-24"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (props.folder.files.length === 0 && props.folder.folders.length === 0) {
    const hasWriteAccess = Boolean(props.folder.hasFolderWriteAccess);
    const isRootFolder = props.folder.parentFolders.length === 0;

    return (
      <>
        <div
          className={classNames(
            "h-96 flex justify-center items-center flex-col rounded-md border-2 border-dashed transition-colors",
            isSurfaceDragOver
              ? "border-persian-500 bg-persian-50"
              : "border-transparent",
          )}
          {...surfaceHandlers}
        >
          <FolderOpenIcon className="h-12 w-12 text-gray-500/70" />
          <p className="m-0 font-semibold text-gray-700">
            {hasWriteAccess
              ? "You haven't uploaded any documents yet"
              : "Nothing to see here"}
          </p>
          <p className="text-sm text-gray-500 mb-4">
            {hasWriteAccess
              ? canExternalDrop
                ? "Drop files or folders here to upload, or create documents below"
                : "Documents and folders you create will appear here"
              : isRootFolder
                ? "This Data Room is currently empty"
                : "This folder is currently empty"}
          </p>
          {hasWriteAccess && isRootFolder && (
            <Button
              variant="neutral"
              text="Copy folder structure from existing deal"
              size="s"
              icon={CopyIcon}
              onClick={() => setShowCopyStructureModal(true)}
            />
          )}
        </div>
        <CopyFolderStructureModal
          open={showCopyStructureModal}
          onClose={() => setShowCopyStructureModal(false)}
        />
      </>
    );
  }

  return (
    <>
      <FolderContext.Provider
        value={{
          isDragging,
          onItemsDropped,
          registerExternalFolderTarget: registerFolderTarget,
          selectItem,
          editFileId,
          setEditFileId,
          moveFileId,
          setMoveFileId,
          deleteFileId,
          setDeleteFileId,
          editFolderId,
          setEditFolderId,
          moveFolderId,
          setMoveFolderId,
        }}
      >
        <div
          ref={selectionSurfaceRef}
          className={classNames(
            "relative min-h-[calc(100vh-280px)] rounded-md border-2 border-dashed transition-colors select-none",
            isSurfaceDragOver
              ? "border-persian-500 bg-persian-50/40"
              : "border-transparent",
          )}
          {...surfaceHandlers}
          onMouseDown={(e) => {
            if (e.shiftKey) {
              e.preventDefault();
            }
            onSurfaceMouseDown(e);
          }}
        >
          <table className="table-fixed w-full box-border text-left border-collapse block select-none">
            <tbody className="w-full table">
              <DataRoomTableHeader folder={props.folder} />
              <Folders folder={props.folder} />
              <Files folder={props.folder} />
            </tbody>
          </table>
          <div
            ref={marqueeOverlayRef}
            className="pointer-events-none fixed z-50 hidden border border-persian-400 bg-persian-200/30"
          />
        </div>
        <MarqueeGutterStrips
          containerRef={selectionSurfaceRef}
          enabled={Boolean(props.folder)}
          onMouseDown={onGutterMouseDown}
        />
      </FolderContext.Provider>
      <DeleteFile
        open={deleteFileId !== ""}
        dataRoomFileId={deleteFileId}
        onClose={() => {
          setDeleteFileId("");
        }}
      />
      <EditFile
        dataRoomFileId={editFileId}
        open={editFileId !== ""}
        onClose={() => {
          setEditFileId("");
        }}
      />
      <MoveFileModal
        dataRoomFileId={moveFileId}
        open={moveFileId !== ""}
        onClose={() => {
          setMoveFileId("");
        }}
        onMoved={() => {
          props.setSelectedFiles([]);
        }}
      />
      <EditFolder
        dataRoomFolderId={editFolderId}
        open={editFolderId !== ""}
        onClose={() => {
          setEditFolderId("");
        }}
      />
      <MoveFolderModal
        dataRoomFolderId={moveFolderId}
        open={moveFolderId !== ""}
        onClose={() => {
          setMoveFolderId("");
        }}
        onMoved={() => {
          props.setSelectedFolders([]);
        }}
      />
      <MoveItemsModal
        dataRoomFileIds={props.selectedFiles}
        dataRoomFolderIds={props.selectedFolders}
        open={props.triggerMoveItems}
        onClose={() => {
          props.setTriggerMoveItems(false);
          props.setSelectedFiles([]);
          props.setSelectedFolders([]);
        }}
      />
      <DeleteItemsModal
        dataRoomFileIds={props.selectedFiles}
        dataRoomFolderIds={props.selectedFolders}
        open={props.triggerDeleteItems}
        onClose={() => {
          props.setTriggerDeleteItems(false);
          props.setSelectedFiles([]);
          props.setSelectedFolders([]);
        }}
        onDelete={() => {
          props.setTriggerDeleteItems(false);
          props.setSelectedFiles([]);
          props.setSelectedFolders([]);
        }}
      />
    </>
  );
}
